import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const TinyTimPageContext = createContext(null);

/**
 * Pages that want Tiny Tim to be aware of richer state (e.g. current Ghost
 * Recon query + results, current creative session, current offer) call
 * `setPageContext(key, value)` once on mount or whenever state of interest
 * changes. The Tiny Tim chat panel and the Telegram surface read this and
 * forward it to the backend with each user message.
 *
 * Pages that don't opt in still get coarse context for free — the URL path
 * is included automatically.
 */
const PAGE_LABELS = [
  ['/dashboard', 'dashboard'],
  ['/my-day', 'my_day'],
  ['/pipeline', 'pipeline'],
  ['/creative-engine', 'creative_engine'],
  ['/creative', 'creative'],
  ['/campaigns', 'campaigns'],
  ['/offers', 'offers'],
  ['/ghost-recon', 'ghost_recon'],
  ['/recommendations', 'recommendations'],
  ['/velocity', 'velocity'],
  ['/campaign-health', 'campaign_health'],
  ['/pnl', 'pnl'],
  ['/predictions', 'predictions'],
  ['/creative-intel', 'creative_intel'],
  ['/operators', 'operators'],
  ['/operations', 'operations'],
  ['/abh-engine', 'abh_engine'],
  ['/abh/attribution', 'abh_attribution'],
  ['/abh/audience', 'abh_audience'],
  ['/abh/ab-tests', 'abh_ab_tests'],
  ['/abh/email', 'abh_email'],
  ['/settings', 'settings'],
];

const labelForPath = (pathname) => {
  if (!pathname) return null;
  // Longest-prefix match so /abh/attribution beats /abh
  const sorted = [...PAGE_LABELS].sort((a, b) => b[0].length - a[0].length);
  const hit = sorted.find(([prefix]) => pathname === prefix || pathname.startsWith(prefix + '/'));
  return hit ? hit[1] : pathname.replace(/^\//, '').split('/')[0] || 'unknown';
};

export const TinyTimContextProvider = ({ children }) => {
  // Keyed bag — each page stores its own slice; we merge on read.
  const [bag, setBag] = useState({});
  const seqRef = useRef(0);

  const setPageContext = useCallback((key, value) => {
    seqRef.current += 1;
    const seq = seqRef.current;
    setBag((prev) => ({
      ...prev,
      [key]: value == null ? undefined : { value, seq, ts: Date.now() },
    }));
  }, []);

  const clearPageContext = useCallback((key) => {
    setBag((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ bag, setPageContext, clearPageContext }),
    [bag, setPageContext, clearPageContext]
  );

  return <TinyTimPageContext.Provider value={value}>{children}</TinyTimPageContext.Provider>;
};

/** Hook for pages that want to publish their state to Tiny Tim. */
export const useTinyTimPageContext = () => {
  const ctx = useContext(TinyTimPageContext);
  if (!ctx) {
    // Provider missing → no-op so pages don't blow up if mounted standalone.
    return { setPageContext: () => {}, clearPageContext: () => {} };
  }
  return { setPageContext: ctx.setPageContext, clearPageContext: ctx.clearPageContext };
};

/**
 * Hook used by the chat panel + Telegram-pairing flow to read the merged
 * page + URL context.
 */
export const useTinyTimResolvedContext = () => {
  const ctx = useContext(TinyTimPageContext);
  const location = useLocation();
  return useMemo(() => {
    const page = labelForPath(location?.pathname);
    const merged = {};
    if (ctx?.bag) {
      for (const [k, v] of Object.entries(ctx.bag)) {
        if (v && v.value !== undefined) merged[k] = v.value;
      }
    }
    return {
      page,
      pathname: location?.pathname || null,
      page_context: Object.keys(merged).length ? merged : null,
    };
  }, [ctx?.bag, location?.pathname]);
};

export default TinyTimContextProvider;
